import { createRouter, createWebHistory } from 'vue-router';
import type { Component } from 'vue';
import { isLoggedIn } from '@/utility/auth'; // Importa la funzione di verifica
import Recaptcha from '@/classes/Recaptcha'; // Importa la classe Recaptcha
import ErrorHandler from '@/classes/ErrorHandler';
import * as Sentry from '@sentry/vue';
import { useAuthStore } from '@/stores/authStore';
import { useListStore } from '@/stores/listStore';
import i18n from '@/i18n';
import {
	clearChunkReloadFlag,
	isChunkLoadError,
	reloadOnceForChunkError,
} from '@/utility/chunkReload';
import { bootTrace } from '@/utility/bootTrace';

/**
 * Wraps a dynamic import so that a ChunkLoadError (stale deploy) triggers
 * a single hard reload instead of showing a white page.
 *
 * Il guard anti-loop vive in `@/utility/chunkReload`, condiviso con il listener
 * `vite:preloadError` di main.ts perché i due percorsi possono scattare per lo
 * stesso asset mancante.
 */
function lazyLoad(importFn: () => Promise<{ default: Component }>) {
	return () => {
		bootTrace('chunk: import avviato');
		return importFn()
			.then((mod) => {
				bootTrace('chunk: import risolto');
				return mod;
			})
			.catch((err: unknown) => {
				bootTrace('chunk: import FALLITO', err);
				if (isChunkLoadError(err) && reloadOnceForChunkError()) {
					// Return a never-resolving promise to stop router navigation while the page reloads
					return new Promise<{ default: Component }>(() => {});
				}

				// Reload già tentato (o non applicabile): l'errore prosegue fino a
				// router.onError, che lo segnala e sblocca il loader.
				throw err;
			});
	};
}

const routes = [
	{
		path: '/login',
		alias: ['/de/login', '/en/login'],
		name: 'login',
		component: lazyLoad(() => import('@/views/LoginView.vue')),
	},
	{
		path: '/logout',
		alias: ['/de/logout', '/en/logout'],
		name: 'logout',
		redirect: (to: any) => {
			const isGerman = to.path.startsWith('/de/');
			const isEnglish = to.path.startsWith('/en/');
			return isGerman ? '/de/login' : isEnglish ? '/en/login' : '/login';
		},
	},
	{
		path: '/sportello-territoriale',
		alias: ['/de/gebietsschalter', '/en/territorial-office'],
		name: 'sportello-territoriale',
		component: lazyLoad(() => import('@/views/FormSportelloTerritorialeView.vue')),
	},
	{
		path: '/documenti',
		alias: ['/de/dokumente', '/en/documents'],
		name: 'documenti',
		component: lazyLoad(() => import('@/views/FormSportelloTerritorialeView.vue')),
	},
	{
		path: '/aggiorna-dati-ricontatto',
		alias: ['/de/kontaktdaten-aktualisieren', '/en/update-contact-details'],
		name: 'aggiorna-dati-ricontatto',
		component: lazyLoad(() => import('@/views/FormAggiornaEmailView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		path: '/segnalazione',
		alias: ['/de/meldung', '/en/report'],
		name: 'segnalazione',
		component: lazyLoad(() => import('@/views/FormSegnalazioneView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		path: '/segnalazione/chat/:email/:id',
		alias: ['/de/meldung/chat/:email/:id', '/en/report/chat/:email/:id'],
		name: 'segnalazioneChat',
		component: lazyLoad(() => import('@/views/PingPongSegnalazioneView.vue')),
		meta: { requiresRecaptcha: true },
		props: (route: { name: string }) => ({ name: route.name })
	},
	{
		path: '/segnalazione/chat/operatore/:email/:id',
		alias: ['/de/meldung/chat/operator/:email/:id', '/en/report/chat/operator/:email/:id'],
		name: 'segnalazioneChatOperatore',
		component: lazyLoad(() => import('@/views/PingPongSegnalazioneView.vue')),
		meta: { requiresRecaptcha: true },
		props: (route: { name: string }) => ({ name: route.name })
	},
	{
		path: '/richiesta/chat/:email/:id',
		alias: ['/de/anfrage/chat/:email/:id', '/en/request/chat/:email/:id'],
		name: 'dichiarazioneChat',
		component: lazyLoad(() => import('@/views/PingPongSegnalazioneView.vue')),
		meta: { requiresRecaptcha: true },
		props: (route: { name: string }) => ({ name: route.name })
	},
	{
		path: '/richiesta/chat/operatore/:email/:id',
		alias: ['/de/anfrage/chat/operator/:email/:id', '/en/request/chat/operator/:email/:id'],
		name: 'dichiarazioneChatOperatore',
		component: lazyLoad(() => import('@/views/PingPongSegnalazioneView.vue')),
		meta: { requiresRecaptcha: true },
		props: (route: { name: string }) => ({ name: route.name })
	},
	{
		path: '/area-riservata',
		alias: ['/de/geschutzter-bereich', '/en/reserved-area'],
		name: 'areaRiservata',
		component: lazyLoad(() => import('@/views/AreaRiservataView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		path: '/area-riservata/documenti/:idRappresentanza/:enteDocumento',
		alias: ['/de/geschutzter-bereich/dokumente/:idRappresentanza/:enteDocumento', '/en/reserved-area/documents/:idRappresentanza/:enteDocumento'],
		name: 'areaRiservataDocumenti',
		component: lazyLoad(() => import('@/views/AreaRiservataDocumentiView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		path: '/area-riservata/profilo-utente',
		alias: ['/de/geschutzter-bereich/benutzerprofil', '/en/reserved-area/user-profile'],
		name: 'profiloUtente',
		component: lazyLoad(() => import('@/views/ProfiloUtenteView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	//QUI
	// NB: qui c'era una rotta di redirect che serviva a togliere lo slash finale
	// (`:form(rateizza-un-pagamento/|...)` → `to.path.slice(0, -1)`).
	//
	// Era inutile e dannosa. Inutile perché vue-router, con `strict: false` (default),
	// tollera già lo slash finale: `/servizio/rateizza-un-pagamento/` matcha da sé la
	// rotta `selectPosizioneICP` qui sotto. Dannosa perché quella rotta, avendo uno score
	// più alto, *oscurava* la rotta reale: l'URL con lo slash risolveva a un record senza
	// nome e senza componente, e la navigazione iniziale passava per un redirect che non
	// si chiudeva. Dato che WordPress canonicalizza con il 301 aggiungendo lo slash,
	// quel percorso era l'unico che gli utenti potessero imboccare.
	//
	// La normalizzazione che il redirect garantiva (fullPath senza slash) è ora fatta
	// dove serve davvero, con `normalizePath()` di `@/utility/route`.
	{
	path: '/servizio/:form(rateizza-un-pagamento|rinegozia-un-pagamento|riesame|affissione-manifesti|istanza-contribuente|invito-al-contraddittorio)',
		alias: ['/de/dienstleistung/:form(ratenzahlung-beantragen|neuverhandlung-zahlung|uberprufung|plakatierung|antrag-steuerpflichtiger|aufforderung-zur-stellungnahme)', '/en/service/:form(payment-installment|renegotiate-a-payment|review|bill-posting|taxpayer-request|invitation-to-respond)'],
		name: 'selectPosizioneICP',
		component: lazyLoad(() => import('@/views/SelectPosizioneView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		path: '/servizio/allacciamento-lampade-votive',
		alias: ['/de/dienstleistung/anschluss-von-votivlampen', '/en/service/votive-lamp-connection'],
		name: 'nuovoAllacciamento',
		component: lazyLoad(() => import('@/views/FormNuovoAllacciamentoView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		path: '/servizio/delega-imposta-soggiorno',
		alias: ['/de/dienstleistung/delegation-tourismussteuer', '/en/service/tourist-tax-delegation'],
		name: 'delegaImpostaSoggiorno',
		component: lazyLoad(() => import('@/views/FormDelegaImpostaSoggiorno.vue')),
		meta: { requiresAuth: false, requiresRecaptcha: true },
	},
	{
		path: '/servizio/abilitazione-struttura-ricettiva',
		alias: ['/de/dienstleistung/ermachtigung-beherbergungsstruktur', '/en/service/accommodation-facility-authorization'],
		name: 'abilitazioneStrutturaRicettiva',
		component: lazyLoad(() => import('@/views/FormAbilitazioneStrutturaRicettiva.vue')),
		meta: { requiresAuth: false, requiresRecaptcha: true },
	},
	{
		path: '/servizio/rateizza-un-pagamento/:ws_service/:docs',
		alias: ['/de/dienstleistung/ratenzahlung-beantragen/:ws_service/:docs', '/en/service/payment-installment/:ws_service/:docs'],
		name: 'rateizzazione',
		component: lazyLoad(() => import('@/views/FormRateizzazioneView.vue')),
		meta: { requiresRecaptcha: true },
	},
	{
		path: '/servizio/invito-al-contraddittorio/:ws_service/:docs',
		alias: ['/de/dienstleistung/aufforderung-zur-stellungnahme/:ws_service/:docs', '/en/service/invitation-to-respond/:ws_service/:docs'],
		name: 'invitoContraddittorio',
		component: lazyLoad(() => import('@/views/FormInvitoContraddittorioView.vue')),
		meta: { requiresRecaptcha: true },
	},
	{
		// http://abaco.test:4173/servizio/rinegozia-un-pagamento/CRC/QUJBQ09BODZBMDFMNDA3QToxNDAwMzI1OA==
		path: '/servizio/rinegozia-un-pagamento/:ws_service/:docs',
		alias: ['/de/dienstleistung/neuverhandlung-zahlung/:ws_service/:docs', '/en/service/renegotiate-a-payment/:ws_service/:docs'],
		name: 'proroga',
		component: lazyLoad(() => import('@/views/FormProrogaView.vue')),
		meta: { requiresRecaptcha: true },
	},
	{
		// http://abaco.test:4173/servizio/riesame/CRC/SFVYTFlJODRMMDdaMjEwWjoxMzkxMTExNQ==
		path: '/servizio/riesame/:ws_service/:docs', // crc
		alias: ['/de/dienstleistung/uberprufung/:ws_service/:docs', '/en/service/review/:ws_service/:docs'],
		name: 'riesame',
		component: lazyLoad(() => import('@/views/FormRiesameView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		// http://abaco.test:4173/servizio/affissione-manifesti
	path: '/servizio/affissione-manifesti',
		alias: ['/de/dienstleistung/plakatierung', '/en/service/bill-posting'],
		name: 'affissione-manifesti',
		component: lazyLoad(() => import('@/views/FormAffissioneManifestiView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		// http://abaco.test:4173/servizio/locandine
		path: '/servizio/locandine',
		alias: ['/de/dienstleistung/plakate', '/en/service/posters'],
		name: 'locandine',
		component: lazyLoad(() => import('@/views/FormLocandineView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		// http://abaco.test:4173/servizio/abbonamento-parcheggi
	path: '/servizio/abbonamento-parcheggi',
		alias: ['/de/dienstleistung/parkabonnement', '/en/service/parking-subscription'],
		name: 'abbonamentoParcheggi',
		component: lazyLoad(() => import('@/views/FormAbbonamentoParcheggiView.vue')),
		meta: { requiresRecaptcha: true },
	},
	{
		// http://abaco.test:4173/servizio/mezzo-permanente/0/a00R200000K1GQbIAN
		// http://abaco.test:4173/servizio/mezzo-permanente/0
		//TODO: prevedere che si arrivi senza step
		path: '/servizio/mezzo-permanente/:step?/:id?',
		alias: ['/de/dienstleistung/dauerhaftes-werbemittel/:step?/:id?', '/en/service/permanent-advertising-medium/:step?/:id?'],
		name: 'mezzoPermanente',
		component: lazyLoad(() => import('@/views/FormMezzoPermanenteController.vue')),
		meta: { requiresAuth: true },
	},
	{
		path: '/servizio/variazione-cessazione-mezzo-pubblicitario',
		alias: ['/de/dienstleistung/anderung-beendigung-werbemittel', '/en/service/change-termination-of-advertising-medium'],
		redirect: (to: any) => {
			// Preserva la lingua nel redirect
			const isGerman = to.path.startsWith('/de/');
			const isEnglish = to.path.startsWith('/en/');
			return isGerman 
				? '/de/dienstleistung/dauerhaftes-werbemittel'
				: isEnglish
				? '/en/service/permanent-advertising-medium'
				: '/servizio/mezzo-permanente';
		}
	},
	{
		path: '/servizio/istanza-contribuente/:ws_service/:docs', // TARI
		alias: ['/de/dienstleistung/antrag-steuerpflichtiger/:ws_service/:docs', '/en/service/taxpayer-request/:ws_service/:docs'],
		name: 'istanza-contribuente',
		component: lazyLoad(() => import('@/views/FormIstanzaContribuenteView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
		// http://abaco.test:4173/servizio/stragiudiziale
		path: '/servizio/stragiudiziale',
		alias: ['/de/dienstleistung/aussergerichtlich', '/en/service/out-of-court'],
		name: 'stragiudiziale',
		component: lazyLoad(() => import('@/views/FormStragiudizialeView.vue')),
		meta: { requiresAuth: true, requiresRecaptcha: true },
	},
	{
	path: '/servizio/paga-online',
		alias: ['/de/dienstleistung/online-bezahlen', '/en/service/pay-online'],
		name: 'pagaOnline',
		component: lazyLoad(() => import('@/views/FormPagaOnline.vue')),
		meta: { requiresRecaptcha: true },
	},
	{
		path: '/pagopa-online-precompilato/:codiceEnte/:codiceServizio',
		alias: ['/de/pagopa-online-vorausgefullt/:codiceEnte/:codiceServizio', '/en/pagopa-online-prefilled/:codiceEnte/:codiceServizio'],
		name: 'paga-online-precompilato',
		component: lazyLoad(() => import('@/views/FormPagaOnlinePrecompilato.vue')),
		meta: { requiresRecaptcha: true },
	},
	{
		path: '/servizio/paga-online/pago-pa/esito',
		alias: ['/de/dienstleistung/online-bezahlen/pago-pa/ergebnis', '/en/service/pay-online/pago-pa/result'],
		name: 'pagamento-result',
		component: lazyLoad(() => import('@/views/PagoPaResultView.vue')),
	},
	{
		path: '/:catchAll(.*)',
		name: 'catchAll',
		component: lazyLoad(() => import('@/views/PageNotFoundView.vue')),
	},
];

const router = createRouter({
	routes,
	history: createWebHistory(import.meta.env.BASE_URL),
	linkActiveClass: 'active', // imposta una classe css custom sui link padri e tutti i figli che sono navigati in quel momento
	scrollBehavior(_, _2, savedPosition) {
		// quando torno indietro posizione lo schermo allo stesso punto
		// //console.log(top, from, savedPosition);
		if (savedPosition) return savedPosition;
	},
});

router.afterEach((to, _from, failure) => {
	bootTrace('afterEach', { to: to.fullPath, failure: failure ? String(failure) : null });
	clearChunkReloadFlag();
	// la route corrente accompagna ogni evento successivo, compresi quelli catturati in automatico
	Sentry.setTag('route', String(to.name ?? to.path));
});

// Un errore di navigazione non gestito lascia `router.isReady()` in stato rejected e
// quindi la pagina bloccata sul loader. Prima non veniva segnalato in alcun modo.
router.onError((error, to) => {
	// Senza listener vue-router stampa l'errore da sé; registrandone uno la
	// responsabilità passa a noi. Un errore che rompe la pagina deve restare
	// visibile in console, non solo su Sentry.
	console.error('[router.onError]', to?.fullPath ?? null, error);
	ErrorHandler.handleError(error, { blocksBoot: !router.currentRoute.value.name });
	ErrorHandler.addLog('router.onError', { to: to?.path ?? null });
});

/**
 * Path che, se raggiunti da un utente già autenticato, vengono dirottati sull'area
 * riservata per evitare che inserisca una richiesta dalla form sbagliata.
 */
const REDIRECT_TO_AREA_RISERVATA_IF_LOGGED = new Set([
	'/servizio/rateizza-un-pagamento',
	'/servizio/rateizza-un-pagamento/',
	'/de/dienstleistung/ratenzahlung-beantragen',
	'/de/dienstleistung/ratenzahlung-beantragen/',
	'/en/service/payment-installment',
	'/en/service/payment-installment/',
	'/servizio/rinegozia-un-pagamento',
	'/servizio/rinegozia-un-pagamento/',
	'/de/dienstleistung/neuverhandlung-zahlung',
	'/de/dienstleistung/neuverhandlung-zahlung/',
	'/en/service/renegotiate-a-payment',
	'/en/service/renegotiate-a-payment/',
	'/servizio/riesame',
	'/servizio/riesame/',
	'/de/dienstleistung/uberprufung',
	'/de/dienstleistung/uberprufung/',
	'/en/service/review',
	'/en/service/review/',
	'/servizio/istanza-contribuente',
	'/servizio/istanza-contribuente/',
	'/de/dienstleistung/antrag-steuerpflichtiger',
	'/de/dienstleistung/antrag-steuerpflichtiger/',
	'/en/service/taxpayer-request',
	'/en/service/taxpayer-request/',
	'/servizio/invito-al-contraddittorio',
	'/servizio/invito-al-contraddittorio/',
	'/de/dienstleistung/aufforderung-zur-stellungnahme',
	'/de/dienstleistung/aufforderung-zur-stellungnahme/',
	'/en/service/invitation-to-respond',
	'/en/service/invitation-to-respond/',
	// ... altri path
]);

/** Route accessibili anche senza login, nonostante `requiresAuth`. */
const PUBLIC_ROUTE_NAMES = [
	'segnalazione', 'nuovoAllacciamento', 'riesame', 'selectPosizioneICP',
	'affissione-manifesti', 'istanza-contribuente', 'proroga', 'mezzoPermanente',
	'locandine', 'stragiudiziale',
];

/**
 * Storage e i18n non devono mai poter far fallire un guard: se lo storage è
 * disabilitato (impostazioni privacy, modalità kiosk) un throw qui bloccherebbe la
 * navigazione e con essa il montaggio della form.
 */
function applyLocaleFromPath(path: string): void {
	const locale = path.startsWith('/de/') ? 'de' : path.startsWith('/en/') ? 'en' : 'it';
	i18n.global.locale.value = locale;
	try {
		localStorage.setItem('locale', locale);
	} catch (error) {
		ErrorHandler.addLog('localStorage non disponibile per la locale', error);
	}
}

// Aggiungi il navigation guard globale
router.beforeEach(async (to, from, next) => {
	bootTrace('guard: entrato', { to: to.fullPath, name: String(to.name), from: from.fullPath });

	// Rileva e imposta la lingua in base all'URL
	applyLocaleFromPath(to.path);

	const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
	const mayRedirectWhenLogged = REDIRECT_TO_AREA_RISERVATA_IF_LOGGED.has(to.fullPath);

	// `isLoggedIn()` fa una chiamata di rete che blocca il primo render: interroghiamo
	// l'API solo quando l'esito cambia davvero la navigazione. Sulle route pubbliche
	// la form può essere montata senza aspettare nessuna risposta.
	bootTrace('guard: isLoggedIn()...', { requiresAuth, mayRedirectWhenLogged });
	const isLogged = requiresAuth || mayRedirectWhenLogged ? await isLoggedIn() : false;
	bootTrace('guard: isLoggedIn() ->', isLogged);

	// se sono loggato e vado su una delle seguenti pagine, vado direttamente in area riservata
	// per evitare di inserisce una richiesta dalla form sbagliata
	if (isLogged && mayRedirectWhenLogged) {
		// Preserva la lingua nel redirect
		const isGerman = to.path.startsWith('/de/');
		const isEnglish = to.path.startsWith('/en/');
		const areaRiservataPath = isGerman ? '/de/geschutzter-bereich' : isEnglish ? '/en/reserved-area' : '/area-riservata';
		return next(areaRiservataPath);
	}

	// Verifica ReCaptcha (spostiamo questo controllo prima dell'auth)
	// se sono in sviluppo, non controllo il recaptcha
	const requiresRecaptcha = to.matched.some((record) => record.meta.requiresRecaptcha);
	if (requiresRecaptcha) {
		try {
			Recaptcha.initialize();
		} catch (error) {
			// reCAPTCHA è uno script esterno a Google, bloccabile da firewall aziendali ed
			// estensioni: non deve impedire il render. Il token viene comunque richiesto al
			// momento del submit (interceptor in API.client), dove l'assenza è già gestita.
			console.error('error reCaptcha', error);
			ErrorHandler.addLog('Recaptcha.initialize fallita, si prosegue senza', error);
		}
	}

	// Controlli di autenticazione, alcune pagine sono accessibili anche da non loggati
	if (requiresAuth && !isLogged) {
		if (PUBLIC_ROUTE_NAMES.includes(to.name as string)) {
			bootTrace('guard: next() (rotta pubblica)');
			return next();
		}
		bootTrace('guard: next(false) + redirect a /logout');

		// prima, per sicurezza, ripulisco token, sessione e cache


		const authStore = useAuthStore();
		authStore.logout();
		const listStore = useListStore();
		listStore.$reset();

		// non serve, in teoria gli store hanno già fatto il loro dovere ma, per sicurezza, prevedo una azione aggressiva aggiuntiva
		try {
			sessionStorage.clear();
			localStorage.clear();
		} catch (error) {
			ErrorHandler.addLog('Pulizia storage non riuscita', error);
		}


		// se restituisco semplicemente la rotta login vue carica la pagina ma non ha contenuto, devo fare fisicamente redirect a /login
		// così interviene il routing di wp che smisterà la richiesta al login di wordpress
		// Preserva la lingua nel redirect
		const isGerman = to.path.startsWith('/de/');
		const isEnglish = to.path.startsWith('/en/');
		window.location.href = isGerman ? '/de/logout' : isEnglish ? '/en/logout' : '/logout';

		return next(false);
	}

	bootTrace('guard: next()');
	next();
});

export default router;